import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, ChevronRightIcon } from 'lucide-react';
import { Tooltip, BarChart, DropoffChart } from './Charts';
import { metrics, weeklyReads, chapterDropoffs, advancedMetrics } from './constants';
import type { Book } from './types';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
interface OverviewTabProps {
  viewMode: string;
  selectedBook: Book;
}
export const OverviewTab = ({
  viewMode,
  selectedBook
}: OverviewTabProps) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  return <div className="space-y-6" data-id="element-6097" data-mp="2o95c35wngybv">
      {/* Reading Trends Chart */}
      <div className={`rounded-lg shadow-sm p-6 border ${dk.card}`} data-id="element-6098" data-mp="2oaq3imno415i">
        <div className="flex items-center justify-between mb-6" data-id="element-6099" data-mp="2t7qdh8nn1o1w">
          <div data-id="element-6100" data-mp="mgqxafpek9zo">
            <h2 className={`text-lg font-medium ${dk.textPrimary}`} data-id="element-6101" data-mp="mibpvzs5piuw">
              Pages Read Over Time
            </h2>
            <p className={`text-sm ${dk.textMuted}`} data-id="element-6102" data-mp="rfbwcbsx4nmj">
              {viewMode === 'all' ? 'Daily reading activity across all books' : `Daily reading activity for ${selectedBook.title}`}
            </p>
          </div>
          <div className="flex space-x-2" data-id="element-6103" data-mp="10lny963zlipy">
            <button className={`px-3 py-1 text-xs font-medium rounded-md ${rt.bgMedium} ${rt.textPrimary}`} data-id="element-6104" data-mp="10kcv98id8ezw">
              Daily
            </button>
            <button className={`px-3 py-1 text-xs font-medium rounded-md ${dk.badgeNeutral}`} data-id="element-6105" data-mp="7x8mcsg5jgiu">
              Weekly
            </button>
            <button className={`px-3 py-1 text-xs font-medium rounded-md ${dk.badgeNeutral}`} data-id="element-6106" data-mp="hudd75ky005z">
              Monthly
            </button>
          </div>
        </div>
        <BarChart data={weeklyReads} data-id="element-6107" data-mp="1t9tno65zjvw4" />
      </div>

      {/* Drop-off Points Chart */}
      <div className={`rounded-lg shadow-sm p-6 border ${dk.card}`} data-id="element-6108" data-mp="1tbmupmkigi4n">
        <div className="flex items-center justify-between mb-6" data-id="element-6109" data-mp="1oa7atxu2khnu">
          <div data-id="element-6110" data-mp="1wc6dvpco6b5s">
            <h2 className={`text-lg font-medium ${dk.textPrimary}`} data-id="element-6111" data-mp="1wamxlyr0k040">
              Reader Drop-off Points
            </h2>
            <p className={`text-sm ${dk.textMuted}`} data-id="element-6112" data-mp="20b6xvb6vvjud">
              {viewMode === 'all' ? 'Where readers stop reading across your catalog' : `Where readers stop reading ${selectedBook.title}`}
            </p>
          </div>
          <div className={`flex items-center text-sm text-red-600`} data-id="element-6113" data-mp="1co75gl5kvg3j">
            <span className="font-medium" data-id="element-6114" data-mp="1hmiewurynol8">
              Main drop-off: Chapter{' '}
              {viewMode === 'all' ? '7' : selectedBook.mainDropoffChapter}
            </span>
            <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-6115" data-mp="1mjim590jqxdx" />
          </div>
        </div>
        <DropoffChart data={chapterDropoffs} data-id="element-6116" data-mp="1rdmspr5wh3eh" />
      </div>

      {/* All Metrics Grid */}
      <div className={`rounded-lg shadow-sm p-6 border ${dk.card}`} data-id="element-6117" data-mp="twem7igb9990">
        <h2 className={`text-lg font-medium mb-6 ${dk.textPrimary}`} data-id="element-6118" data-mp="yv94mjad8vsl">
          All Metrics
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6119" data-mp="rcrnps05uepl">
          {metrics.map((metric, index) => <div key={index} className={`flex items-start space-x-4 p-4 rounded-xl hover:shadow-md transition ${dk.surfaceMuted}`} data-id="element-6120" data-mp="mdyam0oscppy" data-mpi={index}>
              <div className="mt-1" data-id="element-6121" data-mp="ce7g3z086apv">{metric.icon}</div>
              <div data-id="element-6122" data-mp="chlgj1pbw9uo">
                <div className="flex items-center" data-id="element-6123" data-mp="8ddvbei2xc17">
                  <h3 className={`font-semibold ${dk.textPrimary}`} data-id="element-6124" data-mp="8eymcu56qcub">
                    {metric.title}
                  </h3>
                  <Tooltip content={metric.description} data-id="element-6125" data-mp="3ut88sxpk1asg" />
                </div>
                <p className={`text-sm mb-1 ${dk.textSecondary}`} data-id="element-6126" data-mp="3ur478mf4onvc">
                  {metric.description}
                </p>
                <div className="flex items-center justify-between" data-id="element-6127" data-mp="3kv13xd8jij6t">
                  <span className={`font-medium text-lg ${dk.textPrimary}`} data-id="element-6128" data-mp="3ku9degtsvbne">
                    {metric.value}
                  </span>
                  {metric.isPositive !== null && <span className={`text-xs flex items-center ${metric.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-6129" data-mp="9f8vb4u7u0y7">
                      {metric.isPositive ? <ArrowUpIcon className="h-3 w-3 mr-1" data-id="element-6130" data-mp="kb0ttbf6g4r" /> : <ArrowDownIcon className="h-3 w-3 mr-1" data-id="element-6131" data-mp="4m5ymnu56bds" />}
                      {metric.change}
                    </span>}
                </div>
              </div>
            </div>)}
        </div>
      </div>

      {/* Advanced Analytics Section */}
      <div className={`rounded-lg shadow-sm p-6 border ${dk.card}`} data-id="element-6132" data-mp="3dv0dnki0t14q">
        <h2 className={`text-2xl font-bold mb-2 ${dk.textPrimary}`} data-id="element-6133" data-mp="3hvahaqicbfgf">
          Advanced Author Analytics
        </h2>
        <p className={`mb-8 max-w-3xl ${dk.textSecondary}`} data-id="element-6134" data-mp="1c2c28nv837dv">
          Go beyond page reads with deep insights, smart marketing tools, and
          reader engagement data built for indie authors.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6135" data-mp="30j3xzfiwr04d">
          {advancedMetrics.map((metric, idx) => <div key={idx} className={`rounded-xl p-4 flex items-start space-x-4 shadow-sm hover:shadow-md transition ${dk.surfaceMuted}`} data-id="element-6136" data-mp="3aaq6h43v2l3f" data-mpi={idx}>
              <div data-id="element-6137" data-mp="3k95w12n8pmat">{metric.icon}</div>
              <div data-id="element-6138" data-mp="3k84j0smnypqg">
                <h3 className={`font-semibold ${dk.textPrimary}`} data-id="element-6139" data-mp="3k7md14a78x5c">
                  {metric.title}
                </h3>
                <p className={`text-sm ${dk.textSecondary}`} data-id="element-6140" data-mp="3fc71iiznzdc3">
                  {metric.description}
                </p>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
};